// Need props spreading for react table v7
/* eslint-disable react/jsx-props-no-spreading */
/* eslint max-lines: ["error", 1500]*/
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */

/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { PropsWithChildren, ReactElement, useEffect, useState } from "react";
import {
    CellProps,
    HeaderProps,
    Hooks,
    Row,
    SortingRule,
    TableInstance,
    TableOptions,
    useColumnOrder,
    useExpanded,
    useFilters,
    useGroupBy,
    usePagination,
    useResizeColumns,
    useRowSelect,
    useSortBy,
    useTable,
    useRowState,
    TableState,
    ActionType
} from "react-table";
import pull from "lodash/pull";
import { CheckBox } from "components/Novus/CheckBox";
import { EmptyStates } from "@arcadis-intelligence/novus-design-system";
import { TableWrapper, TableThead, TableBodyRow } from "./styles";
import SortDesc from "../../../assets/sortDesc.svg";
import SortAsc from "../../../assets/sortAsc.svg";
import FilterImg from "../../../assets/filter-icon.svg";
import { SkeletonLoader } from "../SkeletonLoader";
import DescendingOrderIcon from "../../../assets/desending-order-icon.svg";
import AscendingOrderIcon from "../../../assets/ascending-order-icon.svg";

/* eslint max-lines-per-function: ["error", 500]*/
export interface Table<T extends object = {}> extends TableOptions<T> {
    name: string;
    onClick?: (row: Row<T>) => void;
    initialPageIndex?: number;
    handlePageChange?: (selectedItem: { selected: number }) => void;
    pageSize?: number;
    isFetchingData?: boolean;
    showRowsPerPageInfo?: boolean;
    rowCount?: number;
    showCheckBoxColumn?: boolean;
    getInstanceCallback?: (arg0: TableInstance) => void;
    onSort?: (params: { sortBy: SortingRule<T>[]; pageSize: number | undefined }) => void;
    filterPosition?: (id: string) => void;
    onRowSelect?: (param1: Record<string, boolean>, param2: TableInstance) => void;
    useControlledState?: (state: TableState) => void;
    customSingleCheckboxLogic?: (row: any, props: any) => void;
    customSelectAllCheckboxLogic?: (props: any) => void;
    resetCondition?: boolean;
    regulationListViewState?: any;
    selectPreviouslySelectedRows?: (instance: any) => void;
    storeSelectedRowsDataInStore?: (
        param1: number[],
        param2: number[],
        param3: number[],
        param4: number[],
        param5: string[],
        param6: number[],
        instance: any
    ) => void;
}

const selectionHook = (hooks: Hooks<any>): void => {
    hooks.allColumns.push((columns) => [
        {
            Cell: (props: CellProps<any>): ReactElement => {
                const { row } = props;

                return (
                    <CheckBox
                        boxShadowColor="none"
                        {...row.getToggleRowSelectedProps()}
                        checked={row.isSelected}
                        dataTestId={`table-checkbox-${row.id}`}
                        onChange={(): void => {
                            if ((props as any).customSingleCheckboxLogic) {
                                (props as any).customSingleCheckboxLogic(row, props);
                            } else {
                                row.toggleRowSelected();
                            }
                        }}
                    />
                );
            },
            Header: (props: HeaderProps<any>): ReactElement => {
                const { getToggleAllRowsSelectedProps } = props;

                return (
                    <div>
                        <CheckBox
                            {...getToggleAllRowsSelectedProps()}
                            dataTestId="select-all-table-checkbox"
                            onChange={(): void => {
                                if ((props as any).customSelectAllCheckboxLogic) {
                                    (props as any).customSelectAllCheckboxLogic(props);
                                } else {
                                    (props as any).toggleAllPageRowsSelected();
                                }
                            }}
                        />
                    </div>
                );
            },
            id: "selector",
            maxWidth: 45,
            minWidth: 30,
            width: 30
        },
        ...columns
    ]);
};

// Code to support using instance of table in the parent component
const useInstance = (instance: TableInstance): void => {
    if (instance && instance.getInstanceCallback) {
        instance.getInstanceCallback(instance);
    }
};

const instanceHook = (hooks: Hooks<any>): void => {
    hooks.useInstance.push(useInstance);
};

let hooks = [
    useColumnOrder,
    useFilters,
    useGroupBy,
    useSortBy,
    useExpanded,
    usePagination,
    useResizeColumns,
    useRowSelect,
    selectionHook,
    instanceHook,
    useRowState
];

/* eslint max-lines-per-function: ["error", 500]*/
export function Table<T extends object>({
    children,
    data,
    isFetchingData,
    columns,
    getInstanceCallback,
    initialPageIndex,
    pageSize,
    rowCount,
    showCheckBoxColumn,
    onSort,
    filterPosition,
    useControlledState,
    onRowSelect,
    customSingleCheckboxLogic,
    customSelectAllCheckboxLogic,
    resetCondition,
    regulationListViewState,
    selectPreviouslySelectedRows,
    storeSelectedRowsDataInStore
}: PropsWithChildren<Table<T>>): ReactElement {
    // Show checkboxes only if user wants it
    if (!showCheckBoxColumn) {
        hooks = pull(hooks, selectionHook);
    }
    const pageCount = rowCount && pageSize ? Math.floor(rowCount / pageSize) : 1;
    const [sortStates, setSortStates] = useState<any>({});
    const instance = useTable(
        {
            columns,
            customSelectAllCheckboxLogic,
            customSingleCheckboxLogic,
            data,
            getInstanceCallback,
            initialState: {
                mainData: {
                    ...regulationListViewState
                },
                pageIndex: initialPageIndex,
                pageSize,
                selectedRowIdsPersist: []
            },
            manualFilters: true,
            manualPagination: true,
            manualSortBy: true,
            pageCount,
            stateReducer: (newState: TableState, action: ActionType) => {
                switch (action.type) {
                    case "SET_SELECTED_ROW_IDS_PERSIST": {
                        return {
                            ...newState,
                            selectedRowIdsPersist: action.payload.selectedRowIdsPersist
                        };
                    }
                    case "SET_SELECTED_ROW_IDS": {
                        return {
                            ...newState,
                            selectedRowIds: action.payload.selectedRowIds
                        };
                    }
                    case "SET_MAIN_DATA": {
                        return {
                            ...newState,
                            mainData: action.payload.mainData
                        };
                    }

                    default:
                        return newState;
                }
            },
            useControlledState
        } as any,
        ...hooks
    );

    const { state } = instance;

    const { pageIndex, sortBy } = instance.state;
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, dispatch } = instance;

    const {
        state: { selectedRowIds }
    } = instance;

    const { mainData } = state;

    useEffect(() => {
        if (onSort) {
            onSort({
                pageSize,
                sortBy
            });
        }
    }, [pageIndex, sortBy]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        dispatch({
            payload: {
                selectedRowIds: []
            },
            type: "SET_SELECTED_ROW_IDS"
        });
        dispatch({
            payload: {
                mainData: {
                    actionsData: [],
                    batchIds: [],
                    curbLinkData: [],
                    isPublishedData: [],
                    parentLinkData: [],
                    regulationIdData: [],
                    rowIdData: []
                }
            },
            type: "SET_MAIN_DATA"
        });
        dispatch({
            payload: {
                selectedRowIdsPersist: []
            },
            type: "SET_SELECTED_ROW_IDS_PERSIST"
        });
        instance.setSortBy([]);
    }, [resetCondition]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (selectPreviouslySelectedRows) {
            selectPreviouslySelectedRows(instance);
        }
    }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (storeSelectedRowsDataInStore) {
            storeSelectedRowsDataInStore(
                mainData.rowIdData,
                mainData.isPublishedData,
                mainData.parentLinkData,
                mainData.curbLinkData,
                mainData.actionsData,
                mainData.batchIds,
                instance
            );
        }
    }, [mainData]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (onRowSelect) {
            onRowSelect(selectedRowIds, instance);
        }
    }, [selectedRowIds]); // eslint-disable-line react-hooks/exhaustive-deps

    const showIcons = (column: any): any => {
        if (column.isSorted) {
            if (column.isSortedDesc) {
                return <img alt="sort descending" src={SortDesc} width="15" />;
            }

            return <img alt="sort ascending" src={SortAsc} width="15" />;
        }

        return null;
    };

    const allFilterDivs = document.querySelectorAll(".filterDiv");

    useEffect(() => {
        const elements = document.getElementsByClassName("filterDiv");

        for (let i = 0; i < elements.length; i++) {
            (elements[i] as HTMLElement).style.display = "none";
        }
    }, []);

    const onFilterIconClick = (id: string): void => {
        allFilterDivs.forEach((currentValue, currentIndex) => {
            (document.querySelectorAll(".filterDiv")[currentIndex] as HTMLElement).style.display =
                "none";
            (document.querySelectorAll(".filterDiv")[currentIndex] as HTMLElement).classList.remove(
                "showArrow"
            );
        });
        (document.getElementById(id) as HTMLElement).style.display = "block";
        (document.getElementById(id) as HTMLElement).classList.add("showArrow");
        if (filterPosition) {
            filterPosition(id);
        }
    };

    const showTitle = (cellId: string): string | undefined => {
        const cellDiv = document.getElementById(cellId);

        return cellDiv?.innerText;
    };

    const handleSort = (column: any) => {
        if (typeof column.isSortedDesc === "undefined" || !column.isSortedDesc) {
            column.toggleSortBy(true, false);
        } else if (column.isSortedDesc || typeof column.isSortedDesc === "undefined") {
            column.toggleSortBy(false, false);
        }
    };

    return isFetchingData ? (
        <div className="p-3">
            <SkeletonLoader count={10} height={40} />
        </div>
    ) : (
        <div className="d-flex flex-column overflow-auto flex-fill">
            {children}
            <TableWrapper {...getTableProps()}>
                <TableThead>
                    {headerGroups.map((headerGroup) => {
                        const { role, ...headerGroupProps } = headerGroup.getHeaderGroupProps();

                        return (
                            <tr {...headerGroupProps}>
                                {headerGroup.headers.map((column: any) => {
                                    const { render, isSorted, isSortedDesc } = column;
                                    const isAscOrDesc = isSortedDesc ? "desc" : "asc";
                                    const extraClass = isSorted ? isAscOrDesc : "";

                                    const toggleSortState = (id: any) => {
                                        setSortStates({
                                            ...sortStates,
                                            [id]: !sortStates[id]
                                        });
                                    };

                                    return (
                                        <th key={column.id} className={extraClass}>
                                            <div className="d-flex align-items-center justify-content-between table-header">
                                                <b>{render("Header")}</b>

                                                {column.canFilter && column.Filter ? (
                                                    <div>
                                                        <span className="me-2">
                                                            {showIcons(column)}
                                                        </span>

                                                        {!column.filterParam.includes(
                                                            "permits"
                                                        ) && (
                                                            <img
                                                                alt="Ascending"
                                                                className="cursor-pointer"
                                                                height={20}
                                                                src={
                                                                    sortStates[column.id]
                                                                        ? DescendingOrderIcon
                                                                        : AscendingOrderIcon
                                                                }
                                                                title={
                                                                    !sortStates[column.id]
                                                                        ? "Sort Descending"
                                                                        : "Sort Ascending"
                                                                }
                                                                width={20}
                                                                onClick={() => {
                                                                    toggleSortState(column.id);
                                                                    handleSort(column);
                                                                }}
                                                                onKeyDown={(event) => {
                                                                    if (event.key === "Enter") {
                                                                        toggleSortState(column.id);
                                                                        handleSort(column);
                                                                    }
                                                                }}
                                                            />
                                                        )}
                                                        <b
                                                            className="filter-icon"
                                                            data-event="click"
                                                            data-for={column.id}
                                                            data-tip={true}
                                                            role="button"
                                                            tabIndex={-1}
                                                            onClick={(): void =>
                                                                onFilterIconClick(column.id)
                                                            }
                                                            onKeyDown={(): void =>
                                                                onFilterIconClick(column.id)
                                                            }
                                                        >
                                                            <img
                                                                alt="sort"
                                                                height="20"
                                                                src={FilterImg}
                                                                width="20"
                                                            />
                                                            {column.render("Filter")}
                                                        </b>
                                                    </div>
                                                ) : null}
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </TableThead>
                <tbody {...getTableBodyProps()} className="flex-fill">
                    {rows.length > 0 ? (
                        rows.map((row) => {
                            prepareRow(row);
                            const { role, ...rowProps } = row.getRowProps();

                            return (
                                <TableBodyRow isSelected={row.isSelected} {...rowProps}>
                                    {row.cells.map((cell) => {
                                        const { role, ...cellProps } = cell.getCellProps();

                                        return (
                                            <td {...cellProps}>
                                                <p
                                                    id={cell.column.id + cell.row.id}
                                                    title={showTitle(cell.column.id + cell.row.id)}
                                                >
                                                    {cell.render("Cell")}
                                                </p>
                                            </td>
                                        );
                                    })}
                                </TableBodyRow>
                            );
                        })
                    ) : (
                        <div className="no-data-text">
                            <EmptyStates
                                description="Oops...This information is not available at the moment."
                                heading="No Files"
                                noFiles={true}
                            />
                        </div>
                    )}
                </tbody>
            </TableWrapper>
        </div>
    );
}
